import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultItemPackage = () => {
  return {
    _id: '_new',
    number: '_new',
    name: '',
    active: true,
    createdBy: null,
    updatedBy: null,
    createdAt: null,
    updatedAt: null,
    items: [],
  };
};

const getDefaultState = () => {
  return {
    itemPackages: [],
    itemPackageCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an item package list view
 * used for paginated item package queries
 * @type {DocumentNode}
 */
const ITEM_PACKAGE_LIST_REQUEST_FIELDS = gql`
  fragment itemPackageListFields on ItemPackage {
    _id
    number
    name
    active
    createdBy {
      _id
      firstname
      lastname
    }
    updatedBy {
      _id
      firstname
      lastname
    }
    createdAt
    updatedAt
  }
`;

/**
 * Defines the fields which are requested for an item package
 * used for all queries/mutations which return an item package
 * @type {DocumentNode}
 */
const ITEM_PACKAGE_REQUEST_FIELDS = gql`
  fragment itemPackageFields on ItemPackage {
    _id
    number
    name
    active
    createdBy {
      _id
      firstname
      lastname
    }
    updatedBy {
      _id
      firstname
      lastname
    }
    items {
      quantity
      item {
        _id
        number
        description
        unit
        type
        solarPlant
      }
    }
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} itemPackage
 */
const createPayload = (itemPackage) => {
  const payload = {
    name: itemPackage.name,
    active: itemPackage.active,
    items: itemPackage.items.map((pkgItem) => ({
      item: pkgItem.item._id || pkgItem.item,
      quantity: pkgItem.quantity,
    })),
  };

  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered item package list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchItemPackagesPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query itemPackagesPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ItemPackageFilterInput
          ) {
            itemPackagesPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              itemPackages {
                ...itemPackageListFields
              }
              itemPackageCount
            }
          }
          ${ITEM_PACKAGE_LIST_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_ITEM_PACKAGES', response.data.itemPackagesPaginated.itemPackages);
      commit('SET_ITEM_PACKAGE_COUNT', response.data.itemPackagesPaginated.itemPackageCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchItemPackageByNumber({ commit, dispatch }, itemPackageNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query itemPackageByNumber($itemPackageNumber: Int!) {
            itemPackageByNumber(itemPackageNumber: $itemPackageNumber) {
              ...itemPackageFields
            }
          }
          ${ITEM_PACKAGE_REQUEST_FIELDS}
        `,
        variables: {
          itemPackageNumber: parseInt(itemPackageNumber),
        },
      });
      commit('ADD_OR_UPDATE_ITEM_PACKAGE', response.data.itemPackageByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createItemPackage({ commit, dispatch }, itemPackage) {
    const payload = createPayload(itemPackage);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createItemPackage($payload: ItemPackageInput!) {
            createItemPackage(itemPackageInput: $payload) {
              ...itemPackageFields
            }
          }
          ${ITEM_PACKAGE_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Paket gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ITEM_PACKAGE', '_new'); // remove temporary _new itemPackage
      commit('ADD_OR_UPDATE_ITEM_PACKAGE', response.data.createItemPackage);
      return response.data.createItemPackage.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateItemPackage({ commit, dispatch }, itemPackage) {
    const payload = createPayload(itemPackage);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateItemPackage($itemPackageNumber: Int!, $payload: ItemPackageInput!) {
            updateItemPackage(itemPackageNumber: $itemPackageNumber, itemPackageInput: $payload) {
              ...itemPackageFields
            }
          }
          ${ITEM_PACKAGE_REQUEST_FIELDS}
        `,
        variables: {
          itemPackageNumber: itemPackage.number,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Paket aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_ITEM_PACKAGE', response.data.updateItemPackage);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteItemPackage({ commit, dispatch }, itemPackageNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteItemPackage($itemPackageNumber: Int!) {
            deleteItemPackage(itemPackageNumber: $itemPackageNumber)
          }
        `,
        variables: {
          itemPackageNumber: itemPackageNumber,
        },
      });
      dispatch('message', {
        message: 'Paket gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ITEM_PACKAGE', itemPackageNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new item package object in store and return it
   * @param {*} param0
   */
  async initItemPackage({ commit }) {
    commit('ADD_OR_UPDATE_ITEM_PACKAGE', defaultItemPackage());
  },

  itemPackageLocalUpdate({ commit }, itemPackage) {
    commit('ADD_OR_UPDATE_ITEM_PACKAGE', itemPackage);
  },

  itemPackageCleanup({ commit }) {
    commit('REMOVE_ITEM_PACKAGE', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEM_PACKAGE_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEM_PACKAGES: (state, itemPackages) =>
    itemPackages ? (state.itemPackages = itemPackages) : (state.itemPackages = []),
  ADD_ITEM_PACKAGE: (state, itemPackage) => state.itemPackages.push(itemPackage),
  ADD_OR_UPDATE_ITEM_PACKAGE: (state, updatedItemPackage) => {
    const existingItemPackage = state.itemPackages.find(
      (itemPackage) => itemPackage._id === updatedItemPackage._id
    );
    if (existingItemPackage) {
      Object.assign(existingItemPackage, updatedItemPackage);
    } else {
      state.itemPackages.push(updatedItemPackage);
    }
  },
  REMOVE_ITEM_PACKAGE: (state, itemPackageId) => {
    const index = state.itemPackages.findIndex(
      (itemPackage) => itemPackage._id === itemPackageId || itemPackage.number === itemPackageId
    );
    if (index >= 0) {
      state.itemPackages.splice(index, 1);
    }
  },
  UPDATE_ITEM_PACKAGE: (state, updatedItemPackage) => {
    const itemPackage = state.itemPackages.find(
      (itemPackage) => itemPackage._id === updatedItemPackage._id
    );
    Object.assign(itemPackage, updatedItemPackage);
  },
  SET_ITEM_PACKAGE_COUNT: (state, itemPackageCount) => (state.itemPackageCount = itemPackageCount),
};

/**
 * Getters
 */
const getters = {
  getItemPackages: (state) => state.itemPackages,
  getItemPackage: (state) => (itemPackageId) =>
    state.itemPackages.find((itemPackage) => itemPackage._id == itemPackageId),
  getItemPackageByNumber: (state) => (itemPackageNumber) =>
    state.itemPackages.find((itemPackage) => itemPackage.number == itemPackageNumber),
  getItemPackageCount: (state) => state.itemPackageCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
